import { formatUnits } from 'viem'

import type { InterfaceToken } from '@morpho-org/dapp-core'

import type { WalletType } from '@/lib/observability/shared/wallet-type'

import type { MarketLendEntryPoint, MarketLendWithdrawSource } from '../events'
import { trackAnalyticsEvent } from '../track'

export type { MarketLendEntryPoint }

export interface MarketLendCompletedParams {
  marketId: string
  loanAssetSymbol: string
  loanAssetAddress: string
  lendAmountRaw: string
  lendAmountUsd: number
  chainId: number
  chainName: string
  walletType: WalletType
  entryPoint: MarketLendEntryPoint
  source?: MarketLendWithdrawSource
}

export function trackMarketLendCompleted(params: MarketLendCompletedParams) {
  trackAnalyticsEvent('market_lend_completed', {
    market_id: params.marketId,
    loan_asset_symbol: params.loanAssetSymbol,
    loan_asset_address: params.loanAssetAddress,
    lend_amount_raw: params.lendAmountRaw,
    lend_amount_usd: params.lendAmountUsd,
    chain_id: params.chainId,
    chain_name: params.chainName,
    wallet_type: params.walletType,
    entry_point: params.entryPoint,
    source: params.source
  })
}

export interface BuildMarketLendCompletedArgs {
  marketId: string
  loanAsset: InterfaceToken | null | undefined
  lendAmount: bigint
  chainId: number
  chainName: string
  walletType: WalletType
  entryPoint: MarketLendEntryPoint
  source?: MarketLendWithdrawSource
}

/**
 * Assemble the `market_lend_completed` payload from raw inputs, or `null` when there is
 * nothing to report — the supply-side payload builder.
 */
export function buildMarketLendCompletedParams({
  marketId,
  loanAsset,
  lendAmount,
  chainId,
  chainName,
  walletType,
  entryPoint,
  source
}: BuildMarketLendCompletedArgs): MarketLendCompletedParams | null {
  if (lendAmount <= 0n || !loanAsset?.symbol) return null

  return {
    marketId,
    loanAssetSymbol: loanAsset.symbol,
    loanAssetAddress: loanAsset.address,
    lendAmountRaw: lendAmount.toString(),
    lendAmountUsd: Number(formatUnits(loanAsset.toUsd(lendAmount) ?? 0n, 18)),
    chainId,
    chainName,
    walletType,
    entryPoint,
    source
  }
}

export interface MarketLendWithdrawCompletedParams {
  marketId: string
  loanAssetSymbol: string
  loanAssetAddress: string
  withdrawAmountRaw: string
  withdrawAmountUsd: number
  /** Full exit via the shares path (position closed to zero). */
  isFullExit: boolean
  chainId: number
  chainName: string
  walletType: WalletType
  entryPoint: MarketLendEntryPoint
  source: MarketLendWithdrawSource
}

export function trackMarketLendWithdrawCompleted(params: MarketLendWithdrawCompletedParams) {
  trackAnalyticsEvent('market_lend_withdraw_completed', {
    market_id: params.marketId,
    loan_asset_symbol: params.loanAssetSymbol,
    loan_asset_address: params.loanAssetAddress,
    withdraw_amount_raw: params.withdrawAmountRaw,
    withdraw_amount_usd: params.withdrawAmountUsd,
    is_full_exit: params.isFullExit,
    chain_id: params.chainId,
    chain_name: params.chainName,
    wallet_type: params.walletType,
    entry_point: params.entryPoint,
    source: params.source
  })
}

export interface BuildMarketLendWithdrawCompletedArgs {
  marketId: string
  loanAsset: InterfaceToken | null | undefined
  withdrawAmount: bigint
  isFullExit: boolean
  chainId: number
  chainName: string
  walletType: WalletType
  entryPoint: MarketLendEntryPoint
  source: MarketLendWithdrawSource
}

/**
 * Assemble the `market_lend_withdraw_completed` payload from raw inputs, or `null` when
 * there is nothing to report — the withdraw-side payload builder.
 */
export function buildMarketLendWithdrawCompletedParams({
  marketId,
  loanAsset,
  withdrawAmount,
  isFullExit,
  chainId,
  chainName,
  walletType,
  entryPoint,
  source
}: BuildMarketLendWithdrawCompletedArgs): MarketLendWithdrawCompletedParams | null {
  if (withdrawAmount <= 0n || !loanAsset?.symbol) return null

  return {
    marketId,
    loanAssetSymbol: loanAsset.symbol,
    loanAssetAddress: loanAsset.address,
    withdrawAmountRaw: withdrawAmount.toString(),
    withdrawAmountUsd: Number(formatUnits(loanAsset.toUsd(withdrawAmount) ?? 0n, 18)),
    isFullExit,
    chainId,
    chainName,
    walletType,
    entryPoint,
    source
  }
}

/** Borrow/Lend toggle on the market rate chart, the entry point to the read-only lend rate. */
export function trackMarketRateModeChanged(params: {
  marketId: string
  chainId: number
  rateMode: 'borrow' | 'supply'
}) {
  trackAnalyticsEvent('market_rate_mode_changed', {
    market_id: params.marketId,
    chain_id: params.chainId,
    rate_mode: params.rateMode
  })
}
